/**
 * GovernmentDepartmentsList — /government/departments
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Landmark, MapPin, PlusCircle, Loader2, ShieldCheck } from "lucide-react";
import { http } from "@/lib/api";
import GovernmentCategoryNav from "@/components/GovernmentCategoryNav";

export default function GovernmentDepartmentsList() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  useEffect(() => { http.get("/government/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const p = Object.fromEntries([...params.entries()]);
    http.get("/government/departments", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [params]);

  const level    = params.get("level") || "";
  const category = params.get("category") || "";
  const country  = params.get("country") || "";
  const city     = params.get("city") || "";
  const q        = params.get("q") || "";
  const levelLabel = (s) => meta?.levels?.find(l => l.slug === s)?.label || s;

  return (
    <>
      <GovernmentCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="departments-list-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">DEPARTMENTS</div>
            <h1 className="font-display text-4xl mb-1">
              {level ? levelLabel(level) + " departments" : "Government departments"}
            </h1>
            <p className="opacity-70">{total} listed</p>
          </div>
          <Link to="/government/departments/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-dept">
            <PlusCircle className="w-4 h-4" /> Register department
          </Link>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          <select value={level} onChange={e => setFilter("level", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-level">
            <option value="">All levels</option>
            {meta?.levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
          </select>
          <select value={category} onChange={e => setFilter("category", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-category">
            <option value="">All categories</option>
            {meta?.department_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <input value={country} onChange={e => setFilter("country", e.target.value)} placeholder="Country"
            className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={city} onChange={e => setFilter("city", e.target.value)} placeholder="City"
            className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={q} onChange={e => setFilter("q", e.target.value)} placeholder="Search…"
            className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]" style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 inline-flex gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <p className="opacity-70 mb-3">No departments match. Be the first to register.</p>
            <Link to="/government/departments/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Register department
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(d => (
              <Link key={d.id} to={`/government/departments/${d.slug}`}
                    className="wofy-card !p-4 hover:shadow-md transition block"
                    data-testid={`dept-card-${d.slug}`}>
                <div className="flex items-center gap-3 mb-2">
                  {d.logo_url ? <img src={d.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                    <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
                      {(d.name || "?")[0].toUpperCase()}
                    </div>}
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-1">
                      {d.name}
                      {d.verified && <ShieldCheck className="w-3 h-3 text-green-600" />}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                      <Landmark className="w-3 h-3 inline mr-1" />{levelLabel(d.level)}
                    </div>
                  </div>
                </div>
                {d.description && <p className="text-sm opacity-80 line-clamp-2">{d.description}</p>}
                {(d.city || d.country) && (
                  <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                    <MapPin className="w-3 h-3" />{[d.city, d.state, d.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
